import type { Place } from './places';

/**
 * TypeScript types for GroupDiscussion component system
 *
 * This module provides comprehensive type definitions for the GroupDiscussion component,
 * which supports multiple discussion contexts including group chats, event discussions,
 * and public building chats.
 * 
 * @example Basic Usage
 * ```tsx
 * import { GroupDiscussion } from '@/components/GroupDiscussion';
 * import { CommentType } from '@/types/groupDiscussion';
 * 
 * // Group discussion
 * <GroupDiscussion
 *   groupId="group-123"
 *   commentType={CommentType.GroupComment}
 *   allowReactions={true}
 *   onComposeComment={(replyTo) => navigation.navigate('Compose', { replyTo })}
 * />
 * 
 * // Public building chat
 * <GroupDiscussion
 *   buildingId="building-456"
 *   commentType={CommentType.GroupComment}
 *   publicChat={true}
 *   enableMarketplaceModerator={true}
 * />
 * ```
 * 
 * @example Advanced Configuration
 * ```tsx
 * const groupMembers: GroupMember[] = [
 *   {
 *     uuid: 'member-1',
 *     user: { uuid: 'user-1', firstName: 'John', lastName: 'Doe' },
 *     role: 10, // Community manager
 *     joinedAt: '2023-01-01T00:00:00Z'
 *   }
 * ];
 * 
 * <GroupDiscussion
 *   groupId="group-123"
 *   commentType={CommentType.GroupComment}
 *   groupMembers={groupMembers}
 *   allowPinning={user.role === 10} // Only community managers
 *   maxCommentLength={2000}
 *   maxImages={5}
 *   header={<CustomHeader />}
 *   onUserProfile={(userId) => router.push(`/profile/${userId}`)}
 * />
 * ```
 * 
 * Key Features:
 * - Nested comments with infinite depth
 * - Emoji reactions with optimistic updates
 * - Image uploads and moderation
 * - Content moderation and reporting
 * - Pin/unpin comments for moderators
 * - Real-time updates with TanStack Query
 * - Infinite scrolling with performance optimization
 * - Full accessibility support
 * - Customizable theming via Tamagui
 * 
 * Performance Features:
 * - Memoized components to prevent unnecessary re-renders
 * - Virtual scrolling with FlashList
 * - Optimistic updates for reactions and replies
 * - Intelligent data fetching with caching
 * - Background refresh and polling
 * 
 * These types are based on the analysis of the legacy app structure
 * and adapted for the new optimized implementation
 */

/**
 * Represents a user in the discussion system
 * 
 * @example
 * ```tsx
 * const user: User = {
 *   uuid: 'user-123',
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   displayName: 'John D.',
 *   email: 'john@example.com',
 *   profileImage: 'https://example.com/avatar.jpg',
 *   verified: true,
 *   building: {
 *     uuid: 'building-456',
 *     name: 'Sunset Towers'
 *   }
 * };
 * ```
 */
export interface User {
  deactivated: any;
  /** Unique identifier for the user */
  uuid: string;
  /** User's first name */
  firstName: string;
  /** User's last name */
  lastName: string;
  /** Formatted display name (usually first name + last initial) */
  displayName: string;
  /** User's email address */
  email: string;
  /** URL to user's profile image */
  profileImage?: string;
  /** Whether the user has been verified by moderators */
  verified?: boolean;
  /** Whether the user's phone number has been verified */
  verifiedPhone?: boolean;
  /** Building information where the user resides */
  building?: {
    uuid: string;
    name: string;
  };
}

// Group member with role information
export interface GroupMember {
  uuid: string;
  user: User;
  role: number; // 0 = member, 10 = community manager, etc.
  joinedAt: string;
}

// Comment reaction information
export interface CommentReaction {
  uuid: string;
  reactionName: string; // 'like', 'love', 'clap', 'smile', 'laugh'
  user: User;
  createdAt: string;
}

/**
 * Represents a comment in the discussion system
 * Supports nested replies, reactions, moderation, and rich content
 * 
 * @example Basic Comment
 * ```tsx
 * const comment: GroupComment = {
 *   uuid: 'comment-123',
 *   contents: 'Great discussion everyone! 👍',
 *   user: userObject,
 *   parent: undefined, // Top-level comment
 *   removed: false,
 *   createdAt: '2023-12-01T10:00:00Z',
 *   updatedAt: '2023-12-01T10:00:00Z',
 *   hidden: false,
 *   type: 'comment',
 *   editingReport: false,
 *   flagged: false,
 *   pinComment: false,
 *   disableReplies: false,
 *   passedmanualmoderation: true,
 *   isPublicFacing: true,
 *   greatContent: false,
 *   children: [],
 *   myReaction: 'like',
 *   reactionsCount: 5,
 *   byReactionUserIds: {
 *     'like': ['user-1', 'user-2'],
 *     'love': ['user-3']
 *   }
 * };
 * ```
 * 
 * @example Reply Comment
 * ```tsx
 * const reply: GroupComment = {
 *   // ... same fields as above
 *   parent: 'comment-123', // UUID of parent comment
 *   contents: '@john Thanks for starting this discussion!',
 * };
 * ```
 */
export interface GroupComment {
  /** Unique identifier for the comment */
  uuid: string;
  /** Text content of the comment (supports markdown) */
  contents: string;
  /** User who created the comment */
  user: User;
  /** UUID of parent comment (for replies) */
  parent?: string;
  
  // Moderation fields
  /** Whether the comment has been removed */
  removed: boolean;
  /** Who removed the comment (if removed) */
  removedBy?: 'USER' | 'ADMIN' | null;
  /** Reason for removal */
  removalReason?: string;
  /** Whether the comment is hidden from public view */
  hidden: boolean;
  /** Who hid the comment */
  hiddenBy?: string;
  /** Whether the comment has been flagged for review */
  flagged: boolean;
  /** Whether the comment is pinned to the top */
  pinComment: boolean;
  /** Whether replies are disabled for this comment */
  disableReplies: boolean;
  /** Whether the comment passed manual moderation */
  passedmanualmoderation: boolean;
  /** Who moderated the comment */
  moderatedBy?: string;
  /** Original content before moderation edits */
  originalContents?: string;
  /** Whether the comment is visible to all users */
  isPublicFacing: boolean;
  /** Whether marked as high-quality content */
  greatContent: boolean;
  
  // Timestamps
  /** When the comment was created */
  createdAt: string;
  /** When the comment was last updated */
  updatedAt: string;
  
  // AI/ML fields
  /** Comment type classification */
  type: string;
  /** AI sentiment analysis score (-1 to 1) */
  sentimentScore?: number;
  /** AI confidence rating (0 to 1) */
  confidenceRating?: number;
  /** Whether the comment is being edited by moderation */
  editingReport: boolean;
  
  // Nested structure
  /** Child comments (replies) */
  children: GroupComment[];
  
  // Reaction data
  /** Current user's reaction to this comment */
  myReaction?: string;
  /** Total number of reactions */
  reactionsCount: number;
  /** Mapping of reaction types to user IDs */
  byReactionUserIds: Record<string, string[]>;
  /** Full reaction objects (optional, for detailed display) */
  reactions?: CommentReaction[];
  
  // Media support
  /** Images attached to the comment */
  images?: CommentImage[];
  
  // Association fields
  /** Associated group ID */
  group?: string;
  /** Associated group event ID */
  groupEvent?: string;

  /**
   * Set when this feed post shares a Place. The backend returns the fully
   * populated place object (not just an id) plus the post type, so the embed
   * renders directly off these — no separate fetch.
   */
  place?: Place;
  placePostType?: PlaceShareType;
}

/** The three ways a place can be shared into the building-wide feed */
export type PlaceShareType = 'want_to_go' | 'question' | 'recommend';

/** Verdict at scan time, not current visibility. */
export type ScanVerdict =
  | 'pending'
  | 'clear'
  | 'questionable'
  | 'scan_failed'
  | 'auto_hidden'
  | 'skipped';

// Comment image structure
export interface CommentImage {
  uuid: string;
  url: string;
  thumbnailUrl?: string;
  width?: number;
  height?: number;
  alt?: string;
  hidden?: boolean;
  scanVerdict?: ScanVerdict;
}

const SILENT_VERDICTS: readonly ScanVerdict[] = ['scan_failed', 'pending'];

// `hidden` gates; the verdict only suppresses.
export function isHidden(image: CommentImage): boolean {
  return (
    image.hidden === true && !SILENT_VERDICTS.includes(image.scanVerdict as ScanVerdict)
  );
}

// Comment creation/update data
export interface CommentInput {
  contents: string;
  parent?: string; // UUID of parent comment for replies
  images?: File[] | any[]; // Image files to upload
  groupId?: string;
  groupEventId?: string;
  confidence?: number;
  tag_name?: string;
  wasHidden?: boolean;
  /** Optional override to proceed after moderation warning */
  userDecision?: 'SEND_ANYWAY';
  /** Session editing report */
  editingReport?: string;
  eventDate?: string;
}

// Comment types enum
export enum CommentType {
  GroupComment = 'groupComment',
  GroupEventComment = 'groupEventComment',
  WelcomeMessage = 'WELCOME_MSG',
  NewUsers = 'NEW_USERS'
}

// Pagination data for comments
export interface CommentPagination {
  page: number;
  limit: number;
  hasMore: boolean;
  total?: number;
}

// Props for the main GroupDiscussion component
export interface GroupDiscussionProps {
  // Core data
  comments?: GroupComment[];
  pinnedComments?: GroupComment[];
  groupMembers?: GroupMember[];

  // Configuration
  buildingId?: string;
  groupId?: string;
  groupEventId?: string;
  eventDate?: string; // Event date for group event comments (YYYY-MM-DD format)
  commentType: CommentType;
  isGroup?: boolean;
  publicChat?: boolean;
  enableMarketplaceModerator?: boolean;

  // Deep linking
  targetCommentId?: string;
  scrollToComment?: boolean;
  onDeepLinkLoadingChange?: (loading: boolean) => void;
  skipInitialDeepLinkRefresh?: boolean;
  
  // State
  loading?: boolean;
  replyLoading?: string; // UUID of comment being replied to
  isBottomLoaderVisible?: boolean;
  
  // Pagination
  showLoadMoreBtn?: boolean;
  showLoadMoreBtnToLoadMore?: boolean;
  
  // Callbacks
  onCreateComment?: (comment: CommentInput) => Promise<void>;
  onUpdateComment?: (commentId: string, updates: Partial<CommentInput>) => Promise<void>;
  onDeleteComment?: (comment: GroupComment) => Promise<void>;
  onReactToComment?: (commentId: string, reaction: string | null) => Promise<void>;
  onReportUser?: (userId: string, reason: string) => Promise<void>;
  onLoadMore?: () => Promise<void>;
  onRefresh?: () => Promise<void>;
  
  // Navigation/Modal callbacks
  onComposeComment?: (replyTo?: GroupComment) => void;
  onShowAllMembers?: () => void;
  onUserProfile?: (userId: string) => void;
  
  // Customization
  // header?: React.ReactNode;
  // emptyState?: React.ReactNode;
  maxCommentLength?: number;
  maxImages?: number;
  
  // Feature flags
  allowReactions?: boolean;
  allowReplies?: boolean;
  allowEditing?: boolean;
  allowPinning?: boolean;
  allowReporting?: boolean;
  allowImageUploads?: boolean;
}

// Props for individual Comment component
export interface CommentComponentProps {
  comment: GroupComment;
  isParent: boolean;
  isChild: boolean;
  isLastComment?: boolean;
  showVerticalLine?: 'short' | 'long' | 'none';
  
  // User info
  currentUserId: string;
  groupMembers: GroupMember[];
  
  // Callbacks
  onReply?: (comment: GroupComment) => void;
  onReact?: (commentId: string, reaction: string | null) => void;
  onEdit?: (comment: GroupComment) => void;
  onDelete?: (comment: GroupComment) => void;
  onReport?: (userId: string) => void;
  onUserProfile?: (userId: string) => void;
  onHidePinned?: (commentId: string) => void;
  
  // Configuration
  allowReactions?: boolean;
  allowReplies?: boolean;
  allowEditing?: boolean;
  allowReporting?: boolean;
  
  // State
  expandReplies?: boolean;
  replyLoading?: boolean;
}

// Props for CommentList component
export interface CommentListProps {
  comments: GroupComment[];
  pinnedComments?: GroupComment[];
  currentUserId: string;
  groupMembers: GroupMember[];
  
  // State
  loading?: boolean;
  refreshing?: boolean;
  replyLoading?: string;
  
  // Pagination
  hasMore?: boolean;
  onLoadMore?: () => void;
  
  // Callbacks
  onRefresh?: () => void;
  onCommentAction?: (action: string, comment: GroupComment) => void;
  
  // Configuration
  commentType: CommentType;
  showPinnedComments?: boolean;
  maxVisibleComments?: number;
  
  // UI customization
  // header?: React.ReactNode;
  // emptyState?: React.ReactNode;
  // loadingComponent?: React.ReactNode;
}

// Hook return type for useGroupDiscussion
export interface UseGroupDiscussionReturn {
  // Data
  comments: GroupComment[];
  pinnedComments: GroupComment[];
  commentCount: number;
  
  // State
  loading: boolean;
  refreshing: boolean;
  replyLoading: string | null;
  hiddenPinnedComments: Record<string, boolean>;
  
  // Actions
  createComment: (input: CommentInput) => Promise<void>;
  updateComment: (commentId: string, updates: Partial<CommentInput>) => Promise<void>;
  deleteComment: (comment: GroupComment) => Promise<void>;
  reactToComment: (commentId: string, reaction: string | null) => Promise<void>;
  reportUser: (userId: string, reason: string) => Promise<void>;
  
  // UI Actions
  hidePinnedComment: (commentId: string) => void;
  refreshComments: () => Promise<void>;
  loadMoreComments: () => Promise<void>;
  
  // Pagination
  hasMoreComments: boolean;
  isLoadingMore: boolean;
  togglePinComment: (commentId: string, isPinned: boolean) => Promise<void>;
  flagComment: (commentId: string) => Promise<void>;
  
  // Error handling
  error: string | null;
  clearError: () => void;
}

// API response types
export interface CommentsResponse {
  comments: GroupComment[];
  hasMore: boolean;
  total: number;
  page: number;
}

export interface CreateCommentResponse {
  comment?: GroupComment;
  status?: number;
  moderationType?: 'PROFANITY' | 'SENTIMENT';
  confidence?: number;
  flaggedTerms?: Array<{
    ruleId: string;
    match: string;
    start: number;
    end: number;
  }>;
}

// Error types
export interface CommentError {
  type: 'NETWORK' | 'VALIDATION' | 'PERMISSION' | 'MODERATION';
  message: string;
  details?: any;
}

// Analytics event data
export interface CommentAnalyticsEvent {
  action: 'create' | 'update' | 'delete' | 'react' | 'report' | 'view';
  commentId?: string;
  commentType: CommentType;
  groupId?: string;
  reactionType?: string;
  metadata?: Record<string, any>;
} 